// Automap and overview map. Ported from gamemap.c.
//
// The original keeps two offscreen 8-bit surfaces (automap 520x520, overview
// 200x200) that accumulate explored rooms, and redraws a zoomed tile grid each
// frame. Index values written here are meaningful in the map screen's palette.

import { G, rooms, map } from './state';
import { Get, GetRoom } from './mapgen';
import { TileData, automap_cols } from './tiles';
import { getImg, getAssetScale } from '../render/assets';
import {
  Surface,
  screen,
  blit,
  blitGame,
  DrawRect,
  draw_charOn,
  drawRectOn,
  fillRectOn,
} from '../render/surface';
import { keyHeld } from './input';
import { K_UP, K_DN, K_LT, K_RT, K_SP, TILE, S, SCREEN_W, MAP_W } from './constants';

const tr = Math.trunc;

function sqr(x: number): number {
  return x * x;
}
function dist(x1: number, y1: number, x2: number, y2: number): number {
  return tr(Math.sqrt(sqr(x2 - x1) + sqr(y2 - y1)));
}

let automap: Surface | null = null;
let overview: Surface | null = null;
let full_rend = 0;
let last_player_room = -1;
let t = 0;

// Cached 54x54 tile grid (S(8) px per tile) — rebuilt only when scroll/blink/player changes.
const MAP_CELL = S(8);
const GRID_W = 54 * MAP_CELL;
const GRID_H = 54 * MAP_CELL;
let mapGridCache: Surface | null = null;
let cacheScrollGX = -1;
let cacheScrollGY = -1;
let cacheCheckpoint = -1;
let cacheBlink = -1;
let cachePlayerGX = -1;
let cachePlayerGY = -1;

const roomChkScratch = new Uint8Array(3000);

function cloneSurface(src: Surface): Surface {
  const s = new Surface(src.w, src.h, src.colorKey, null);
  s.pix.set(src.pix);
  return s;
}

export function InitAutomap(): void {
  automap = cloneSurface(getImg('dat/i/automap.png')); // 520x520
  overview = cloneSurface(getImg('dat/i/overview.png')); // 200x200
  mapGridCache = new Surface(GRID_W, GRID_H);
  cacheScrollGX = -1;

  full_rend = 0;
  last_player_room = -1;
  if (G.game_load) {
    if (G.artifacts[0]) {
      FullRender();
    } else {
      for (let i = 0; i < 3000; i++) {
        if (rooms[i].visited) RecordRoom(i);
      }
      RecordRoom(G.player_room);
    }
  }
}

export function FullRender(): void {
  const am = automap!;
  for (let y = 0; y < map.h; y++) {
    for (let x = 0; x < map.w; x++) {
      if (TileData[Get(x, y)].Is_Solid) {
        if (Get(x, y) !== 17) {
          am.pix[am.w * (y + 4) + (x + 4)] = 128;
        }
      }
    }
  }
  for (let i = 0; i < 3000; i++) {
    if (rooms[i].visited) RecordRoom(i);
  }
  RecordRoom(G.player_room);
  full_rend = 1;
}

function DisplayRoom(room_id: number, room_bg: number): void {
  const am = automap!;
  const ov = overview!;
  const rm = rooms[room_id];
  for (let y = 0; y < rm.h; y++) {
    for (let x = 0; x < rm.w; x++) {
      const rx = x + rm.x;
      const ry = y + rm.y;
      const off = am.w * (ry + 4) + (rx + 4);
      let v = automap_cols[TileData[Get(rx, ry)].Type];
      if (v === 192) v = room_bg & 255;
      am.pix[off] = v & 255;
    }
  }
  fillRectOn(
    ov,
    {
      x: tr((rm.x * 200) / MAP_W),
      y: tr((rm.y * 200) / MAP_W),
      w: tr((rm.w * 200) / MAP_W),
      h: tr((rm.h * 200) / MAP_W),
    },
    200
  );
}

export function RecordRoom(room_id: number): void {
  if (last_player_room !== -1) {
    const lr = rooms[last_player_room];
    if (lr.checkpoint) DisplayRoom(last_player_room, 150 - tr(lr.s_dist / 5) * 12);
    else DisplayRoom(last_player_room, 192 - tr(lr.s_dist / 5) * 12);
  }
  last_player_room = room_id;
  DisplayRoom(room_id, 0);
}

export function GetNearestCheckpoint(nx: number, ny: number): number {
  let nearest_checkpoint = -1;
  let nearest_dist = 10000000;
  roomChkScratch.fill(0);

  let i = GetRoom(tr(nx / TILE), tr(ny / TILE));
  if (i !== -1) {
    roomChkScratch[i] = 1;
    if (rooms[i].checkpoint !== 0 && rooms[i].visited !== 0) nearest_checkpoint = i;
  }
  if (nearest_checkpoint === -1) {
    for (let y = 0; y < 54; y += 2) {
      for (let x = 0; x < 54; x += 2) {
        const rx = tr(nx / TILE) - 27 + x;
        const ry = tr(ny / TILE) - 27 + y;
        i = GetRoom(rx, ry);
        if (i !== -1 && roomChkScratch[i] === 0) {
          roomChkScratch[i] = 1;
          if (rooms[i].checkpoint !== 0 && rooms[i].visited !== 0) {
            const cp_x = rooms[i].x * TILE + rooms[i].w * S(16);
            const cp_y = rooms[i].y * TILE + rooms[i].h * S(16);
            const cp_dist = dist(cp_x, cp_y, nx, ny);
            if (cp_dist < nearest_dist) {
              nearest_dist = cp_dist;
              nearest_checkpoint = i;
            }
          }
        }
      }
    }
  }
  return nearest_checkpoint;
}

function drawMapGridCell(
  dest: Surface,
  df_x: number,
  df_y: number,
  grx: number,
  gry: number,
  nearest_checkpoint: number
): void {
  let xcol = 0;
  if (grx >= 0 && gry >= 0 && grx < MAP_W && gry < MAP_W) {
    const groom = GetRoom(grx, gry);
    if (groom >= 0 && rooms[groom].visited) {
      const tile = Get(grx, gry);
      if (G.tele_select && nearest_checkpoint === GetRoom(grx, gry) && tr(t / 3) % 2) {
        xcol = 255;
      }
      const col = automap_cols[TileData[tile].Type];
      drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, col ^ xcol);

      switch (tile) {
        case 25:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, '*'.charCodeAt(0), 0 ^ xcol);
          break;
        case 26:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, '+'.charCodeAt(0), 0 ^ xcol);
          break;
        case 28:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, 'S'.charCodeAt(0), 0 ^ xcol);
          break;
        case 29:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, 'C'.charCodeAt(0), 0 ^ xcol);
          break;
        case 30:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, 'R'.charCodeAt(0), 0 ^ xcol);
          break;
        case 31:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, 'S'.charCodeAt(0), 0 ^ xcol);
          break;
        case 32:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, 29, 0 ^ xcol);
          break;
        case 53:
          drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 192 ^ xcol);
          draw_charOn(dest, df_x, df_y, 28, 0 ^ xcol);
          break;
      }

      if (tile < 12) {
        drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 0 ^ xcol);
        draw_charOn(dest, df_x, df_y, tile + 10, 255 ^ xcol);
      }
      if (tile >= 45 && tile <= 52) {
        drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 0 ^ xcol);
        draw_charOn(dest, df_x, df_y, ((tile - 45) % 4) + 14, 255 ^ xcol);
      }
      if (tile >= 13 && tile < 17) {
        drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 0 ^ xcol);
        draw_charOn(dest, df_x, df_y, tile - 13 + 22, 255 ^ xcol);
      }
      if (tile >= 21 && tile < 25) {
        drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 0 ^ xcol);
        draw_charOn(dest, df_x, df_y, tile - 13 + 22, 255 ^ xcol);
      }
      if (tile >= 38 && tile < 42) {
        drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 0 ^ xcol);
        draw_charOn(dest, df_x, df_y, tile - 13 + 22, 255 ^ xcol);
        draw_charOn(dest, df_x, df_y, 26, 255 ^ xcol);
      }
      if (tr(G.player_x / TILE) === grx && tr(G.player_y / TILE) === gry) {
        drawRectOn(dest, df_x, df_y, MAP_CELL, MAP_CELL, 0 ^ xcol);
        draw_charOn(dest, df_x, df_y, 30, 255 ^ xcol);
      }
    }
  }
}

function rebuildMapGridCache(scrollGX: number, scrollGY: number, nearest_checkpoint: number): void {
  const cache = mapGridCache!;
  fillRectOn(cache, { x: 0, y: 0, w: GRID_W, h: GRID_H }, 255);
  for (let y = 0; y < 54; y++) {
    for (let x = 0; x < 54; x++) {
      const grx = scrollGX - 27 + x;
      const gry = scrollGY - 27 + y;
      drawMapGridCell(cache, x * MAP_CELL, y * MAP_CELL, grx, gry, nearest_checkpoint);
    }
  }
}

export function DisplayAutomap(): void {
  const am = automap!;
  const ov = overview!;

  if (keyHeld[K_UP]) G.c_scroll_y -= TILE + keyHeld[K_SP] * S(64);
  if (keyHeld[K_DN]) G.c_scroll_y += TILE + keyHeld[K_SP] * S(64);
  if (keyHeld[K_LT]) G.c_scroll_x -= TILE + keyHeld[K_SP] * S(64);
  if (keyHeld[K_RT]) G.c_scroll_x += TILE + keyHeld[K_SP] * S(64);

  if (G.c_scroll_x < 0) G.c_scroll_x = 0;
  if (G.c_scroll_y < 0) G.c_scroll_y = 0;
  if (G.c_scroll_x >= MAP_W * TILE) G.c_scroll_x = MAP_W * TILE - TILE;
  if (G.c_scroll_y >= MAP_W * TILE) G.c_scroll_y = MAP_W * TILE - TILE;

  const nearest_checkpoint = GetNearestCheckpoint(G.c_scroll_x, G.c_scroll_y);

  if (G.artifacts[0] && !full_rend) FullRender();

  let minimap_scroll_x = tr(G.c_scroll_x / TILE) - tr(200 / 2);
  let minimap_scroll_y = tr(G.c_scroll_y / TILE) - tr(229 / 2);
  if (minimap_scroll_x < 0) minimap_scroll_x = 0;
  if (minimap_scroll_y < 0) minimap_scroll_y = 0;
  if (minimap_scroll_x >= MAP_W - 200) minimap_scroll_x = MAP_W - 200 - 1;
  if (minimap_scroll_y >= MAP_W - 229) minimap_scroll_y = MAP_W - 229 - 1;

  fillRectOn(screen, { x: S(437), y: S(235), w: S(200), h: S(229) }, 255);
  blitGame(
    am,
    { x: minimap_scroll_x + 4, y: minimap_scroll_y + 4, w: 200, h: 229 },
    screen,
    S(437),
    S(235),
    getAssetScale('dat/i/automap.png')
  );

  let rx = tr(G.c_scroll_x / TILE) - 27 - minimap_scroll_x + S(437);
  let ry = tr(G.c_scroll_y / TILE) - 27 - minimap_scroll_y + S(235);
  DrawRect(rx, ry, S(55), S(1), 50);
  DrawRect(rx, ry + S(54), S(55), S(1), 50);
  DrawRect(rx, ry, S(1), S(55), 50);
  DrawRect(rx + S(54), ry, S(1), S(55), 50);

  blitGame(ov, { x: 0, y: 0, w: 200, h: 200 }, screen, S(437), S(32), getAssetScale('dat/i/overview.png'));

  rx = tr(((tr(G.c_scroll_x / TILE) - 27) * 200) / MAP_W) + S(437);
  ry = tr(((tr(G.c_scroll_y / TILE) - 27) * 200) / MAP_W) + S(32);
  let rw = tr((54 * 200) / MAP_W);
  let rh = tr((54 * 200) / MAP_W);
  if (rx < S(437)) {
    rw -= S(437) - rx;
    rx = S(437);
  }
  if (ry < S(32)) {
    rh -= S(32) - ry;
    ry = S(32);
  }
  if (rx + rw >= S(437) + S(200) - S(1)) rw -= rx + rw - (S(437) + S(200) - S(1));
  if (ry + rh >= S(32) + S(200) - S(1)) rh -= ry + rh - (S(32) + S(200) - S(1));

  DrawRect(rx, ry, rw + S(1), S(1), 50);
  DrawRect(rx, ry + rh, rw + S(1), S(1), 50);
  DrawRect(rx, ry, S(1), rh + S(1), 50);
  DrawRect(rx + rw, ry, S(1), rh + S(1), 50);

  DrawRect(S(2), S(31), S(432), S(432), 255);
  t++;

  const scrollGX = tr(G.c_scroll_x / TILE);
  const scrollGY = tr(G.c_scroll_y / TILE);
  const playerGX = tr(G.player_x / TILE);
  const playerGY = tr(G.player_y / TILE);
  const blink = tr(t / 3) % 2;

  if (
    scrollGX !== cacheScrollGX ||
    scrollGY !== cacheScrollGY ||
    nearest_checkpoint !== cacheCheckpoint ||
    blink !== cacheBlink ||
    playerGX !== cachePlayerGX ||
    playerGY !== cachePlayerGY
  ) {
    cacheScrollGX = scrollGX;
    cacheScrollGY = scrollGY;
    cacheCheckpoint = nearest_checkpoint;
    cacheBlink = blink;
    cachePlayerGX = playerGX;
    cachePlayerGY = playerGY;
    rebuildMapGridCache(scrollGX, scrollGY, nearest_checkpoint);
  }

  blit(mapGridCache!, { x: 0, y: 0, w: GRID_W, h: GRID_H }, screen, S(2), S(31));

  DrawRect(0, S(29), SCREEN_W, S(3), 230);
  DrawRect(0, S(464), SCREEN_W, S(4), 230);
  DrawRect(0, S(29), S(2), S(437), 230);
  DrawRect(S(434), S(29), S(3), S(437), 230);
  DrawRect(S(637), S(29), S(3), S(437), 230);
  DrawRect(S(437), S(232), S(200), S(3), 230);
}
